import { Component, Injector, Input, OnInit } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { filter, first, switchMap, takeUntil, tap } from 'rxjs/operators'
import { of } from 'rxjs'

import {
  Device,
  DeviceActivityLabel,
  DeviceStatus,
  DeviceStatusExt,
  DeviceStatusExtLabel,
  DeviceStatusLabel,
  DeviceUsageLabel,
  DeviceOwnershipLabel,
} from '@shared/models/device.model'
import { DevicesService } from '@shared/modules/devices/devices.service'
import { DeviceListDatasource } from './device-list.datasource'
import { DialogService } from '@shared/dialogs/dialog.service'
import { ApiTableComponent } from '@shared/components/tables/api-table.component'
import { PermissionsService } from '@shared/services/permissions.service'
import { DeviceInstallUninstallDialogComponent } from '@shared/dialogs/device-install-uninstall-dialog/device-install-uninstall-dialog.component'
import { AuthService } from '@app/modules/auth/auth.service'
import { DestroyService } from '@core/services/destroy.service'
import { PermissionSourceType, PermissionsSource } from '@core/types/permissions.source'
import { TeamType } from '@shared/models/team.model'
import { CheckDeviceStatusDialogComponent } from '@shared/dialogs/check-device-status-dialog/check-device-status-dialog.component'
import { BulkUpdateDialogComponent } from '@app/shared/dialogs/bulk-update-dialog/bulk-update-dialog.component'
import { Plan } from '@app/shared/models/plan.model'
import { DataService } from '@app/shared/services/data.service'

@Component({
  selector: 'lin-device-list',
  templateUrl: './device-list.component.html',
  styleUrls: ['./device-list.component.scss'],
  providers: [DestroyService],
  standalone: false,
})
export class DeviceListComponent extends ApiTableComponent implements OnInit {
  devices: Device[] = []
  plans: Plan[]
  form: FormGroup
  dataSource: DeviceListDatasource
  isReseller: boolean

  DeviceStatusLabel = DeviceStatusLabel
  DeviceUsageLabel = DeviceUsageLabel
  DeviceActivityLabel = DeviceActivityLabel
  DeviceStatusExtLabel = DeviceStatusExtLabel
  DeviceOwnershipLabel = DeviceOwnershipLabel
  PermissionsSource = PermissionsSource
  DeviceStatus = DeviceStatus
  DeviceStatusExt = DeviceStatusExt

  selectedDevices = new Map<string, string>()

  @Input() teamId: number
  @Input() isClient: boolean
  @Input() newDeviceUrl: string
  @Input() resellerId: number

  columns = [
    'select',
    'imei',
    'imsi',
    'iccid',
    'phone',
    'client',
    'reseller',
    'chevronAccountId',
    'plan',
    'vendor',
    'model',
    'usage',
    'vehicleRegNo',
    'status',
    'statusExt',
    'isDeactivated',
    'installDate',
    'addedToTeam',
    'ownership',
    'contractStartAt',
    'contractFinishAt',
    'contractId',
    'lastDataReceivedAt',
    'deactivatedAt',
    'edit',
    'install',
    'uninstall',
  ]

  columnsMandatory = ['edit', 'install', 'uninstall']

  protected readonly fileName = 'Devices'
  protected readonly omitStateFields = ['teamId']
  createNewDeviceUrl: string[]

  get showDeviceDeactivationDate() {
    return this.resellerId || (this.authService.getCurrentUser().isReseller() && !this.teamId)
  }

  get isAdminOrReseller(): boolean {
    return this.authService.getCurrentUser().isAdminOrReseller()
  }

  get showChevronIdColumn(): boolean {
    const user = this.authService.getCurrentUser()
    return user.isReseller() && user.team.options?.isChevron
  }

  constructor(
    private dataService: DataService,
    private deviceService: DevicesService,
    private dialog: DialogService,
    private translateService: TranslateService,
    private router: Router,
    private permissionService: PermissionsService,
    private route: ActivatedRoute,
    protected injector: Injector,
    private readonly authService: AuthService,
    private readonly destroyService: DestroyService,
  ) {
    super(injector)

    !this.isBilling && this.authService.getCurrentUser().isClient() && this.filterColumns(['contractFinishAt'])
  }

  ngOnInit(): void {
    super.ngOnInit()

    const user = this.authService.getCurrentUser()

    this.form.addControl('showUnavailable', new FormControl())

    if (user.isAdminOrReseller()) {
      this.addStockStatus('InStockReseller', 'IN_STOCK_RESELLER')
      this.columnsMandatory.push('select')
    }
    if (user.isAdmin()) this.addStockStatus('InStockAdmin', 'IN_STOCK_ADMIN')

    if (this.teamId && !this.resellerId) this.filterColumns('client')
    !this.hasPermission(PermissionsSource['DEVICE.DEVICES_LIST.INSTALL']) && this.filterColumns(['install', 'uninstall'])
    this.isClient && this.filterColumns(['plan', 'vendor', 'model', 'reseller'])
    !this.authService.getCurrentUser().isAdminOrReseller() && this.filterColumns(['select', 'addedToTeam'])
    !this.showDeviceDeactivationDate && this.filterColumns(['deactivatedAt'])
    !this.showChevronIdColumn && this.filterColumns(['chevronAccountId'])

    const plans$ = this.isClient ? of(null) : this.dataService.getPlans().pipe(takeUntil(this.destroyService))

    plans$.pipe(takeUntil(this.destroyService)).subscribe((plans) => {
      plans && (this.plans = plans)

      if (this.newDeviceUrl) {
        this.createNewDeviceUrl = [this.newDeviceUrl]
        this.dataSource = new DeviceListDatasource(this.deviceService, this.sort, this.paginator, this.form, this.resellerId)
      } else {
        this.dataSource = new DeviceListDatasource(this.deviceService, this.sort, this.paginator, this.form)
        this.authService.currentUser$.pipe(first(), takeUntil(this.destroyService)).subscribe((user) => {
          this.isReseller = user.isReseller()
          this.createNewDeviceUrl = user.isReseller() ? ['/reseller/devices/new'] : ['/admin/devices/new']
        })
      }
    })
  }

  protected initForm() {
    super.initForm()
    !this.resellerId && this.form.addControl('teamId', new FormControl(this.teamId || null))
  }

  hasPermission(permissionType: PermissionSourceType): boolean {
    return this.permissionService.hasPermissionSync(permissionType)
  }

  openInstallationDialog(device?: Device, isInstall = false) {
    const clientId = this.route.parent.snapshot.params.id as number
    const dialogTitle = isInstall ? 'ADMIN.DEVICES.INSTALL' : 'ADMIN.DEVICES.UNINSTALL'
    const adminUrl = clientId ? `admin/clients/${clientId}/devices` : 'admin/devices'
    const redirectUrl = this.isClient ? 'client/devices' : adminUrl

    this.dialog
      .open(DeviceInstallUninstallDialogComponent, this.translateService.instant(dialogTitle) as string, { device, install: isInstall })
      .afterClosed()
      .pipe(
        tap(() => void this.router.navigate([redirectUrl])),
        filter(Boolean),
        switchMap((data: Device) => {
          const title = <string>this.translateService.instant('DIALOGS.CHECK_DEVICE_STATUS.TITLE')
          return isInstall
            ? this.dialog.open(CheckDeviceStatusDialogComponent, title, data.deviceInstallation.vehicleId).afterClosed()
            : of(null)
        }),
        switchMap(() => this.dataSource.refresh()),
      )
      .subscribe(() => void this.router.navigate([redirectUrl]))
  }

  getEditDeviceLink(id: string): [string, string] | [string] {
    const { teamType } = this.authService.getCurrentUser()

    if (!teamType) return

    switch (teamType) {
      case TeamType.Admin:
        return ['/admin/devices', id]
      case TeamType.Client:
        return [id]
      case TeamType.Reseller:
        return ['/reseller/devices', id]
    }
  }

  openClientInNewTab(type: string, clientId?: number, resellerId?: number): void {
    const url = this.router.url.substring(0, this.router.url.lastIndexOf('/'))
    const link = type === 'client' ? url + `/clients/${clientId}/company-info` : url + `/resellers/${resellerId}/reseller-info`
    window.open(link, '_blank')
  }

  handleSelectDevice({ id, imei }: Device): void {
    this.selectedDevices.has(id) ? this.selectedDevices.delete(id) : this.selectedDevices.set(id, imei)
  }

  openBulkUpdateDialog(mode: 'contract' | 'ownership'): void {
    const titleKey = mode === 'contract' ? 'ADMIN.DEVICES.UPDATE_CONTRACTS' : 'ADMIN.DEVICES.UPDATE_OWNERSHIPS'
    const title = <string>this.translateService.instant(titleKey)
    this.dialog
      .open(BulkUpdateDialogComponent, title, { devices: this.selectedDevices, mode })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.dataSource.refresh()),
        takeUntil(this.destroyService),
      )
      .subscribe(() => this.selectedDevices.clear())
  }

  private addStockStatus(key: string, statusLocale: string) {
    this.DeviceStatusLabel = Object.assign(
      {
        [DeviceStatus[key]]: <string>this.translateService.instant(`ADMIN.DEVICES.STATUSES.${statusLocale}`),
      },
      this.DeviceStatusLabel,
    )
  }
}
